import React, { useEffect, useMemo, useState } from 'react';
import {
  Ban, Check, CheckCheck, CornerUpLeft, Download, Eye, Filter, Paperclip, Printer, RotateCcw, Search, Send, ShieldCheck, X, XCircle,
} from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { DateInput, Select } from '../../components/Controls';
import { openStored } from '../../components/Files';
import { download, toCSV } from '../../format';
import { Chip } from '../attendance/shared';
import { Empty, STATUS_LABEL, StatusChip, TypeChip, WAITING, dmy, longDate, num, useFrozen } from './shared';

// Leave Management > Leave Requests, Manage Requests and one request's view (Stephanet's leavemgmt/Requests.jsx).
// Every action goes through the LEAVE_REQUEST approval: Pre-Approve is the supervisor step, Approve completes it.

// what each action does, its button and whether it needs a reason
export const ACTIONS = {
  submit: ['Submit', 'Send the request for approval.', Send, 'mds-run', false],
  pre_approve: ['Pre-Approve', 'Supervisor approval; the request then waits for HR final approval.', ShieldCheck, 'mds-run', false],
  approve: ['Approve', 'Approve the leave. The days are taken from the balance and marked on the attendance register.', CheckCheck, 'mds-run lvm-ok', false],
  reject: ['Reject', 'Reject the request. The employee sees the reason.', XCircle, 'mds-run att2-danger', true],
  return: ['Return for Correction', 'Send the request back to the employee to correct and resubmit.', CornerUpLeft, 'mds-run lvm-warn', true],
  cancel: ['Cancel', 'Cancel the leave. Approved days return to the balance and leave marks are removed from attendance.', Ban, 'mds-run att2-danger', false],
};
// actions open at each status (as the server allows them)
export const NEXT = {
  draft: ['submit', 'cancel'], pending: ['pre_approve', 'approve', 'reject', 'return', 'cancel'], pre_approved: ['approve', 'reject', 'return', 'cancel'],
  returned: ['submit', 'cancel'], rejected: ['submit'], approved: ['cancel'],
};
const allowed = (r, can) => (NEXT[r.status] || []).filter(a => (['submit', 'cancel'].includes(a) ? can.edit || can.create : can.approve));

// confirm an action on one or more requests, with the reason when it needs one
export function ActionModal({ rows, action, onClose, onDone }) {
  const { notify } = useHR();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [label, text, Ico, cls, needs] = ACTIONS[action];
  const run = () => {
    setBusy(true);
    hr.leaveAction({ ids: rows.map(r => r.id), action, comment })
      .then(r => {
        if (r.failed.length) notify(`${r.done} done; ${r.failed.length} not: ${r.failed[0]}`, 'bad');
        else notify(`${r.done} leave request(s): ${label.toLowerCase()}`);
        onDone();
      })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false));
  };
  return (
    <Modal className="mdl-modal" title={<><Ico size={18} /> {label} Leave</>} onClose={() => !busy && onClose()}>
      <div className="mdl-modal-body">
        <p className="mdl-modal-lead">{text}</p>
        <div className="lvm-mini-list">
          {rows.slice(0, 6).map(r => <div key={r.id}><b>{r.reference}</b><span>{r.name}</span><TypeChip t={r.leave_type} /><em>{dmy(r.start_date)} → {dmy(r.end_date)} · {r.days}d</em></div>)}
          {rows.length > 6 && <small>…and {rows.length - 6} more</small>}
        </div>
        <label className="mdl-field">{needs ? 'Reason *' : 'Comment (optional)'}
          <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)} placeholder={needs ? 'Why…' : 'Add a note for the employee'} />
        </label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className={cls} disabled={busy || (needs && !comment.trim())} onClick={run}><Ico size={14} /> {busy ? 'Saving…' : `${label}${rows.length > 1 ? ` (${rows.length})` : ''}`}</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Leave Requests (simple register)
export function LeaveRequests({ data, reload, onView, tick }) {
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [acting, setActing] = useState(null);
  const rows = useMemo(() => data.requests.filter(r => (!status || r.status === status) && (!type || r.leave_type === type)
    && (!q || [r.name, r.employee_no, r.reference, r.department].some(v => String(v || '').toLowerCase().includes(q.toLowerCase())))), [data, status, type, q]);
  const ref = useFrozen(2, [tick, rows.length]);
  const types = [...new Set(data.requests.map(r => r.leave_type))].sort();
  return (
    <div className="mdr-card mdr-table-card">
      <div className="mds-table-head">
        <b>Leave Requests</b>
        <div className="att2-filters no-print">
          <div className="mdr-search mds-quick"><Search size={14} /><input placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} /></div>
          <Select value={status} onChange={e => setStatus(e.target.value)}><option value="">All Status</option>{Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
          <Select value={type} onChange={e => setType(e.target.value)}><option value="">All Types</option>{types.map(t => <option key={t} value={t}>{t}</option>)}</Select>
          <span className="att2-count">{rows.length} records</span>
        </div>
      </div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table lvm-frozen lvm-f2" ref={ref}>
          <thead><tr><th className="c no-print">Actions</th><th>Employee</th><th>Type</th><th>Period</th><th className="c">Days</th><th>Applied</th><th>Status</th><th>Approved By</th></tr></thead>
          <tbody key={tick}>
            {rows.map((r, i) => {
              const acts = allowed(r, data.can).filter(a => ['approve', 'pre_approve', 'reject'].includes(a));
              return (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  <td className="c no-print nowrap">
                    <button className="mds-view" onClick={() => onView(r.id)}><Eye size={12} /> View</button>
                    {acts.includes('pre_approve') && <button className="lvm-icon ok" title="Pre-Approve" onClick={() => setActing({ rows: [r], action: 'pre_approve' })}><ShieldCheck size={13} /></button>}
                    {acts.includes('approve') && <button className="lvm-icon ok" title="Approve" onClick={() => setActing({ rows: [r], action: 'approve' })}><Check size={13} /></button>}
                    {acts.includes('reject') && <button className="lvm-icon bad" title="Reject" onClick={() => setActing({ rows: [r], action: 'reject' })}><X size={13} /></button>}
                  </td>
                  <td className="lvm-emp"><b>{r.name}</b><small>{[r.employee_no, r.department, r.grade].filter(Boolean).join(' • ')}</small></td>
                  <td><TypeChip t={r.leave_type} /></td>
                  <td className="lvm-period"><b>{dmy(r.start_date)} → {dmy(r.end_date)}</b><small>{r.reason}</small></td>
                  <td className="c b">{r.days}</td>
                  <td className="nowrap">{dmy(r.applied_on)}</td>
                  <td><StatusChip s={r.status} />{['rejected', 'returned'].includes(r.status) && r.comment && <small className="lvm-why">{r.comment}</small>}</td>
                  <td className="nowrap">{r.approved_by || '—'}</td>
                </tr>
              );
            })}
            {!rows.length && <Empty cols={8}>No leave requests{status || type || q ? ' match these filters' : ' yet'}.</Empty>}
          </tbody>
        </table>
      </div>
      {acting && <ActionModal {...acting} onClose={() => setActing(null)} onDone={() => { setActing(null); reload(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- Manage Requests (full register with bulk actions)
const EMPTY = { status: '', type: '', department: '', q: '', from: '', to: '', paid: '' };
export function ManageRequests({ data, reload, onView, tick, initial }) {
  const [f, setF] = useState(() => ({ ...EMPTY, ...(initial || {}) }));
  const [showFilter, setShowFilter] = useState(!!initial);
  const [picked, setPicked] = useState(new Set());
  const [acting, setActing] = useState(null);
  useEffect(() => { if (initial) { setF({ ...EMPTY, ...initial }); setShowFilter(true); } }, [initial]);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const rows = useMemo(() => data.requests.filter(r => {
    if (f.status === 'waiting' ? !WAITING.includes(r.status) : f.status && r.status !== f.status) return false;
    if (f.type && r.leave_type !== f.type) return false;
    if (f.department && r.department !== f.department) return false;
    if (f.paid && String(r.paid) !== f.paid) return false;
    if (f.from && r.end_date < f.from) return false;
    if (f.to && r.start_date > f.to) return false;
    return !f.q || [r.name, r.employee_no, r.reference, r.email].some(v => String(v || '').toLowerCase().includes(f.q.toLowerCase()));
  }), [data, f]);
  const ref = useFrozen(4, [tick, rows.length]);
  useEffect(() => setPicked(new Set()), [tick]);
  const all = data.requests;
  const pills = [['Total', all.length, ''], ['Approved', all.filter(r => r.status === 'approved').length, 'approved'],
    ['Pending', all.filter(r => WAITING.includes(r.status)).length, 'waiting'], ['Rejected', all.filter(r => r.status === 'rejected').length, 'rejected'],
    ['Unpaid', all.filter(r => !r.paid).length, 'unpaid']];
  const chosen = rows.filter(r => picked.has(r.id));
  const bulk = ['pre_approve', 'approve', 'reject', 'return'].filter(a => data.can.approve && chosen.length && chosen.every(r => (NEXT[r.status] || []).includes(a)));
  const toggle = id => setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = rows.length > 0 && rows.every(r => picked.has(r.id));
  const exportCsv = () => download(`leave-manage-requests-${data.today}.csv`, toCSV([
    { label: 'Leave ID', get: r => r.reference }, { label: 'Employee', get: r => r.name }, { label: 'Employee ID', get: r => r.employee_no },
    { label: 'Email', get: r => r.email }, { label: 'Department', get: r => r.department }, { label: 'Grade', get: r => r.grade },
    { label: 'Leave Type', get: r => r.leave_type }, { label: 'From', get: r => r.start_date }, { label: 'To', get: r => r.end_date },
    { label: 'Working Days', get: r => r.days }, { label: 'Status', get: r => STATUS_LABEL[r.status] }, { label: 'Payment', get: r => (r.paid ? 'Paid' : 'Unpaid') },
    { label: 'Progress %', get: r => r.progress }, { label: 'Credit', get: r => r.credit ?? '' }, { label: 'Taken', get: r => r.taken ?? '' },
    { label: 'Remaining', get: r => r.remaining ?? '' },
  ], rows));
  const types = [...new Set(all.map(r => r.leave_type))].sort();
  return (
    <div className="mdr-card mdr-table-card">
      <div className="mds-table-head">
        <b>Manage Leave Requests</b>
        <div className="att2-filters no-print">
          <button className={`mdr-btn ${showFilter ? 'on' : ''}`} onClick={() => setShowFilter(s => !s)}><Filter size={13} /> Filter</button>
          {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}><Download size={13} /> Export</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={13} /> Print</button>
          <span className="att2-count">{rows.length} records</span>
        </div>
      </div>
      {showFilter && (
        <div className="lvm-filter no-print">
          <label>Search<input value={f.q} onChange={e => set('q', e.target.value)} placeholder="Name, ID, email…" /></label>
          <label>Status<Select value={f.status} onChange={e => set('status', e.target.value)}><option value="">All</option><option value="waiting">Awaiting approval</option>
            {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></label>
          <label>Leave Type<Select value={f.type} onChange={e => set('type', e.target.value)}><option value="">All</option>{types.map(t => <option key={t} value={t}>{t}</option>)}</Select></label>
          <label>Department<Select value={f.department} onChange={e => set('department', e.target.value)}><option value="">All</option>{data.departments.map(d => <option key={d} value={d}>{d}</option>)}</Select></label>
          <label>Payment<Select value={f.paid} onChange={e => set('paid', e.target.value)}><option value="">All</option><option value="true">Paid</option><option value="false">Unpaid</option></Select></label>
          <label>From<DateInput value={f.from} onChange={e => set('from', e.target.value)} /></label>
          <label>To<DateInput value={f.to} onChange={e => set('to', e.target.value)} /></label>
          <button type="button" className="mdr-btn" onClick={() => setF(EMPTY)}><RotateCcw size={13} /> Clear</button>
        </div>
      )}
      <div className="lvm-pills no-print">
        {pills.map(([l, v, key]) => (
          <button key={l} className={`lvm-pill p-${l.toLowerCase()} ${(key === 'unpaid' ? f.paid === 'false' : f.status === key && !f.paid) ? 'on' : ''}`}
                  onClick={() => setF(x => ({ ...x, status: key === 'unpaid' ? '' : key, paid: key === 'unpaid' ? 'false' : '' }))}>{l}: <b>{v}</b></button>
        ))}
      </div>
      {bulk.length > 0 && (
        <div className="lvm-bulk no-print">
          <span><b>{chosen.length}</b> selected</span>
          {bulk.map(a => { const [l, , I, cls] = ACTIONS[a]; return <button key={a} className={`${cls} sm`} onClick={() => setActing({ rows: chosen, action: a })}><I size={13} /> {l}</button>; })}
          <button className="mdr-btn" onClick={() => setPicked(new Set())}>Clear selection</button>
        </div>
      )}
      <div className="mdr-scroll">
        <table className="mdr-table mds-table lvm-frozen lvm-f4" ref={ref}>
          <thead><tr>
            <th className="c no-print"><input type="checkbox" checked={allOn} onChange={() => setPicked(allOn ? new Set() : new Set(rows.map(r => r.id)))} aria-label="Select all" /></th>
            <th className="c no-print">Action</th><th>Leave ID</th><th>Employee</th><th>Dept/Grade</th><th>Leave Type</th><th>Period</th><th className="c">Working Days</th>
            <th>Status</th><th>Payment</th><th>Progress</th><th className="c">Credit</th><th className="c">Taken</th><th className="c">Remaining</th>
          </tr></thead>
          <tbody key={tick}>
            {rows.map((r, i) => (
              <tr key={r.id} className={`mdr-row ${picked.has(r.id) ? 'picked' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                <td className="c no-print"><input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} aria-label={`Select ${r.reference}`} /></td>
                <td className="c no-print"><button className="mds-view" onClick={() => onView(r.id)}><Eye size={12} /> View</button></td>
                <td className="mdr-id">{r.reference}</td>
                <td className="lvm-emp"><b>{r.name}</b><small>{r.employee_no}{r.email ? ` · ${r.email}` : ''}</small></td>
                <td className="lvm-emp"><b className="n">{r.department || '—'}</b><small>{r.grade || '—'}</small></td>
                <td className="nowrap">{r.leave_type}</td>
                <td className="nowrap">{r.start_date} →<br />{r.end_date}</td>
                <td className="c b">{r.days}</td>
                <td><StatusChip s={r.status} /></td>
                <td><Chip tone={r.paid ? 'green' : 'orange'}>{r.paid ? 'Paid' : 'Unpaid'}</Chip></td>
                <td><div className={`lvm-progress s-${r.status}`}><i style={{ width: `${r.progress}%` }} /></div><small className="lvm-pct">{r.progress}%</small></td>
                <td className="c">{num(r.credit)}</td><td className="c am">{num(r.taken)}</td><td className="c g">{num(r.remaining)}</td>
              </tr>
            ))}
            {!rows.length && <Empty cols={14}>No leave requests match these filters.</Empty>}
          </tbody>
        </table>
      </div>
      {acting && <ActionModal {...acting} onClose={() => setActing(null)} onDone={() => { setActing(null); reload(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- one request: details, attachment, approval trail and actions
export function RequestView({ id, data, reload, onClose }) {
  const r = data.requests.find(x => x.id === id);
  const [acting, setActing] = useState(null);
  if (!r) return null;
  const acts = allowed(r, data.can);
  const steps = [['Applied', r.applied_on, true], ['Supervisor', r.pre_approved_by, ['pre_approved', 'approved'].includes(r.status)],
    ['HR Approval', r.approved_by, r.status === 'approved']];
  return (
    <Modal className="mdl-modal lvm-view" wide title={<><Eye size={18} /> {r.reference} — {r.name}</>} onClose={onClose}>
      <div className="mdl-modal-body">
        <div className="lvm-view-head">
          <TypeChip t={r.leave_type} /><StatusChip s={r.status} /><Chip tone={r.paid ? 'green' : 'orange'}>{r.paid ? 'Paid' : 'Unpaid'}</Chip>
          <span className="grow" /><b>{r.days} working day(s)</b>
        </div>
        <div className="lvm-steps">
          {steps.map(([l, who, on], i) => <div key={l} className={on ? 'on' : r.status === 'rejected' && i > 0 ? 'bad' : ''}><span>{i + 1}</span><b>{l}</b><small>{who ? (i === 0 ? dmy(who) : who) : '—'}</small></div>)}
        </div>
        <dl className="lvm-dl">
          <dt>Employee</dt><dd>{r.name} ({r.employee_no})</dd>
          <dt>Department / Grade</dt><dd>{r.department || '—'} · {r.grade || '—'}</dd>
          <dt>Site</dt><dd>{r.site || '—'}</dd>
          <dt>Period</dt><dd>{longDate(r.start_date)} → {longDate(r.end_date)}</dd>
          <dt>Resumption</dt><dd>{longDate(r.resumption)}</dd>
          <dt>Balance ({r.leave_type})</dt><dd>{r.credit === null ? 'Not tracked' : `Credit ${num(r.credit)} · Taken ${num(r.taken)} · Remaining ${num(r.remaining)}`}</dd>
          <dt>Reason</dt><dd>{r.reason || '—'}</dd>
          <dt>Reliever</dt><dd>{r.reliever || '—'}</dd>
          <dt>Emergency contact</dt><dd>{r.emergency_contact || '—'}</dd>
          <dt>Handover note</dt><dd>{r.handover_note || '—'}</dd>
          {r.comment && <><dt>Approver comment</dt><dd>{r.comment}</dd></>}
          <dt>Attachment</dt><dd>{r.files ? <button className="mds-view" onClick={() => openStored(() => hr.leaveAttachmentBlob(r.id))}><Paperclip size={12} /> Open attachment</button> : 'None'}</dd>
        </dl>
        {acts.length > 0 && (
          <div className="mdh-form-foot lvm-view-actions">
            {acts.map(a => { const [l, , I, cls] = ACTIONS[a]; return <button key={a} className={`${cls} sm`} onClick={() => setActing({ rows: [r], action: a })}><I size={13} /> {l}</button>; })}
          </div>
        )}
      </div>
      {acting && <ActionModal {...acting} onClose={() => setActing(null)} onDone={() => { setActing(null); reload(); }} />}
    </Modal>
  );
}
